<template>
  <div class="min-h-screen bg-gray-50">
    <!-- Sidebar -->
    <div
        :class="[
        'fixed inset-y-0 left-0 z-50 w-64 bg-white shadow-xl transform transition-transform duration-300 ease-in-out',
        sidebarOpen ? 'translate-x-0' : '-translate-x-full',
        'lg:translate-x-0'
      ]"
    >
      <!-- Logo -->
      <div class="flex items-center justify-center h-16 px-4 bg-green-600">
        <div class="flex items-center space-x-2">
          <div class="w-15 h-15 bg-white rounded-full flex items-center justify-center">
            <NuxtLink to="/" class="w-10 h-10 bg-white rounded-full flex items-center justify-center">
<!--              <svg class="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 20 20">-->
<!--                <path d="M10.394 2.08a1 1 0 00-.788 0l-7 3a1 1 0 000 1.84L5.25 8.051a.999.999 0 01.356-.257l4-1.714a1 1 0 11.788 1.838L7.667 9.088l1.94.831a1 1 0 00.787 0l7-3a1 1 0 000-1.838l-7-3zM3.31 9.397L5 10.12v4.102a8.969 8.969 0 00-1.05-.174 1 1 0 01-.89-.89 11.115 11.115 0 01.25-3.762zM9.3 16.573A9.026 9.026 0 007 14.935v-3.957l1.818.78a3 3 0 002.364 0l5.508-2.361a11.026 11.026 0 01.25 3.762 1 1 0 01-.89.89 8.968 8.968 0 00-5.35 2.524 1 1 0 01-1.4 0zM6 18a1 1 0 001-1v-2.065a8.935 8.935 0 00-2-.712V17a1 1 0 001 1z"/>-->
<!--              </svg>-->
              <img
                  src="/logo.png"
                  alt="Logo"
                  class="h-full w-auto max-h-full object-contain"
              />
            </NuxtLink>
          </div>
          <div class="text-white">
            <div class="text-sm font-bold">ECO.CONSTRUCTION</div>
            <div class="text-xs opacity-90">SARL</div>
          </div>
        </div>
      </div>

      <!-- Navigation -->
      <nav class="mt-8 px-4 space-y-2 overflow-y-auto" style="height: calc(100vh - 8rem);">
        <div v-for="section in navigation" :key="section.title" class="space-y-2">
          <div v-if="section.title" class="px-3 py-2 text-xs font-semibold text-gray-500 uppercase tracking-wider">
            {{ $t(section.title) }}
          </div>

          <div v-for="item in section.items" :key="item.name" class="space-y-1">
            <!-- Items sans sous-menu -->
            <NuxtLink
                v-if="!item.children"
                :to="item.href"
                @click="sidebarOpen = false"
                :class="[
                'group flex items-center px-3 py-2 text-sm font-medium rounded-lg transition-colors duration-200',
                isActiveRoute(item.href)
                  ? 'bg-green-50 text-green-700 border-r-2 border-green-600'
                  : 'text-gray-700 hover:bg-gray-100 hover:text-green-600'
              ]"
            >
              <i :class="[item.icon,'mr-3 h-5 w-5 flex-shrink-0']"></i>
              {{ $t(item.name) }}
            </NuxtLink>

            <!-- Items avec sous-menu -->
            <div v-else>
              <button
                  @click="toggleSubmenu($t(item.name))"
                  :class="[
                  'group flex items-center w-full px-3 py-2 text-sm font-medium rounded-lg transition-colors duration-200',
                  hasActiveChild(item)
                    ? 'bg-green-50 text-green-700'
                    : 'text-gray-700 hover:bg-gray-100 hover:text-green-600'
                ]"
              >
                <i :class="[item.icon,'mr-3 h-5 w-5 flex-shrink-0']"></i>
                <span class="flex-1 text-left">{{ $t(item.name) }}</span>
                <svg
                    :class="[
                    'ml-auto h-4 w-4 transition-transform duration-200',
                    openSubmenus.includes($t(item.name)) ? 'rotate-90' : 'rotate-0'
                  ]"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                >
                  <path fill-rule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clip-rule="evenodd" />
                </svg>
              </button>

              <!-- Sous-menu -->
              <div v-show="openSubmenus.includes($t(item.name))" class="ml-8 mt-1 space-y-1">

              <NuxtLink
                  v-for="child in item.children"
                  :key="$t(child.name)"
                  :to="child.href"
                  @click="sidebarOpen = false"
                  :class="[
                  'group flex items-center px-3 py-2 text-sm rounded-lg transition-colors duration-200',
                  isActiveRoute(child.href)
                    ? 'bg-green-50 text-green-700 font-medium'
                    : 'text-gray-600 hover:bg-gray-50 hover:text-green-600'
                ]"
              >
                <i :class="[child.icon,'mr-3 h-5 w-5 flex-shrink-0']"></i>
                {{ $t(child.name) }} <Badge :value="child.badge" severity="secondary"></Badge>
              </NuxtLink>
              </div>
            </div>
          </div>
        </div>
      </nav>

      <!-- User Profile -->
      <div class="absolute bottom-0 left-0 right-0 p-4 bg-gray-50 border-t">
        <div class="flex items-center space-x-3">
          <div class="flex-shrink-0">
            <div class="w-8 h-8 bg-green-600 rounded-full flex items-center justify-center">
              <span class="text-sm font-medium text-white">{{ userInitials }}</span>
            </div>
          </div>
          <div class="flex-1 min-w-0">
            <p class="text-sm font-medium text-gray-900 truncate">{{ user?.firstName || '' }} {{ user?.lastName || '' }}</p>
            <p class="text-xs text-gray-500 truncate">{{ user?.role?.name || '' }}</p>
<!--            <p class="text-xs text-gray-500 truncate">{{ $d(new Date(user.createdAt), 'short') }}</p>-->
          </div>
<!--          <button @click="showUserMenu = !showUserMenu" class="p-1 rounded-full hover:bg-gray-200">-->
<!--            <svg class="w-4 h-4 text-gray-500" fill="currentColor" viewBox="0 0 20 20">-->
<!--              <path d="M10 6a2 2 0 110-4 2 2 0 010 4zM10 12a2 2 0 110-4 2 2 0 010 4zM10 18a2 2 0 110-4 2 2 0 010 4z"/>-->
<!--            </svg>-->
<!--          </button>-->
        </div>
      </div>
    </div>

    <!-- Mobile backdrop -->
    <div v-if="sidebarOpen" @click="sidebarOpen = false" class="fixed inset-0 z-40 bg-gray-600 bg-opacity-75 lg:hidden"></div>

    <!-- Main Content -->
    <div class="flex flex-col min-h-screen lg:ml-64">
      <!-- Top Navigation -->
      <div class="bg-white shadow-sm border-b border-gray-200 sticky top-0 z-30">
        <div class="px-4 sm:px-6 lg:px-8">
          <div class="flex justify-between h-16">
            <!-- Left side -->
            <div class="flex items-center">
              <button
                  @click="sidebarOpen = !sidebarOpen"
                  class="p-2 rounded-md text-gray-400 hover:text-gray-500 hover:bg-gray-100 lg:hidden"
              >
                <svg class="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 6h16M4 12h16M4 18h16" />
                </svg>
              </button>

              <!-- Breadcrumbs -->
              <nav class="flex space-x-2 text-sm text-gray-500 ml-4">
                <i class="pi pi-gauge fs-2 h-3 w-3"></i>
                <NuxtLink to="/admin" class="hover:text-green-600 hidden sm:inline">Dashboard</NuxtLink>
                <span>/</span>
                <span class="text-gray-900 font-medium hidden sm:inline">{{ currentPageTitle }}</span>
                <span class=" block sm:hidden"><i :class="[currentPageIcon,'fs-2 h-3 w-3']"></i></span>
              </nav>
            </div>

            <!-- Right side -->
            <div class="flex items-center space-x-4">
              <!-- Notifications -->
<!--              <NotificationBell />-->

              <!-- Language Selector -->
              <InternationalisationButtons />

              <!-- User Menu -->
              <UserActions v-if="isLoggedIn" :user="user" />
            </div>
          </div>
        </div>
      </div>

      <!-- Page Content -->
      <main class="flex-1 overflow-y-auto">
        <div class="px-4 sm:px-6 lg:px-8 py-8">
          <slot />
        </div>
      </main>

      <!-- Indicateurs globaux -->
      <Transition name="fade">
        <div v-if="isCheckingNotifications" class="loading-indicator">
          <Icon name="heroicons:arrow-path" class="animate-spin h-4 w-4" />
          <span>Vérification des notifications...</span>
        </div>
      </Transition>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted } from 'vue'
import { useRoute } from 'vue-router'
import {useAuthStore} from "~/stores/auth";

// Système de notifications
const {
  isLoading: isCheckingNotifications,
  startNotificationCheck,
  stopNotificationCheck
} = useNotifications({
  autoStart: false, // On gérera manuellement selon l'état d'auth
  interval: 30000
})

// État réactif
const sidebarOpen = ref(false)
const showUserMenu = ref(false)
const openSubmenus = ref(['projects']) // Ouvrir par défaut le menu projets

const route = useRoute()

const authStore = useAuthStore()
const RBAC = useRBAC()
// Utilisateur fictif (à remplacer par votre store Pinia)
const currentUser = authStore.user

// Navigation structure
const navigation = computed(()=>[
  {
    title: '',
    items: [
      { name: 'navigation.dashboard', href: '/admin', icon: 'pi pi-gauge' }
    ]
  },
  ...(RBAC.canReadQuote()? [{
    title: 'navigation.projectManagement',
    items: [
      // {
      //   name: 'navigation.projects_.title',
      //   icon: ProjectIcon,
      //   children: [
      //     { name: 'navigation.projects_.list', href: '/admin/projects', icon: 'pi pi-folder' },
      //     { name: 'navigation.projects_.tasks', href: '/admin/tasks', icon: 'pi pi-list-check' }
      //   ]
      // },
      // { name: 'navigation.clients', href: '/admin/clients', icon: 'pi pi-address-book' },
      // { name: 'navigation.documents', href: '/admin/documents', icon: 'pi pi-file' },
      // { name: 'navigation.incidents', href: '/admin/incidents', icon: 'pi pi-ban' },
      {
        name: 'contact.admin.title',
        icon: "pi pi-envelope",
        href: '/admin/contact-requests'
        // children: [
        //   { name: 'contact.admin.list', href: '/admin/contact-requests', icon: 'pi pi-phone' },
        //   { name: 'contact.admin.doc', href: '/admin/contact-requests', icon: 'pi pi-phone' },
        //   // { name: 'navigation.projects_.tasks', href: '/admin/tasks', icon: ProjectIcon }
        // ]
      },
    ]
  }]:[]),
    ...(RBAC.canReadUser() || RBAC.canReadRole() ? [{
    title: 'navigation.administration',
    items: [
      ...(RBAC.canReadUser()?[{ name: 'navigation.users', href: '/admin/users', icon: 'pi pi-users' }]:[]),
      ...(RBAC.canReadRole()?[{ name: 'navigation.roles', href: '/admin/roles', icon: 'pi pi-shield' }]:[]),
      // { name: 'navigation.reports', href: '/admin/reports', icon: 'pi pi-flag-fill' },
      // { name: 'navigation.settings', href: '/admin/settings', icon: 'pi pi-cog' }
    ]
  }]:[]),
  {
    title: 'user.settings',
    items: [
        {
      name: 'user.profile',
      href: '/admin/profile',
      icon: 'pi pi-user',
    }
    ]
  }
])

// Computed
const userInitials = computed(() => {
  const first = currentUser?.firstName?.charAt(0) || ''
  const last = currentUser?.lastName?.charAt(0) || ''
  return (first + last).toUpperCase()
})

const { t } = useI18n()

const isLoggedIn = computed(() => authStore?.isLoggedIn)
const user = computed(() => authStore.user || {})

const currentPageTitle = computed(() => {
  const path = route.path
  if (path === '/admin') return t('navigation.dashboard')
  if (path.includes('/profile')) return t('navigation.profile')
  if (path.includes('/projects')) return t('navigation.projects_.title')
  if (path.includes('/clients')) return t('navigation.clients')
  if (path.includes('/users')) return t('navigation.users')
  if (path.includes('/roles')) return t('navigation.roles')
  if (path.includes('/incidents')) return t('navigation.incidents')
  if (path.includes('/documents')) return t('navigation.documents')
  if (path.includes('/tasks')) return t('navigation.projects_.tasks')
  if (path.includes('/settings')) return t('navigation.settings')
  if (path.includes('/reports')) return t('navigation.reports')
  if (path.includes('/contact-requests')) return t('contact.admin.title')
  return 'Page'
})

const currentPageIcon = computed(() => {
  const path = route.path
  if (path === '/admin') return 'pi pi-gauge'
  if (path.includes('/projects')) return 'pi pi-folder'
  if (path.includes('/clients')) return 'pi pi-address-book'
  if (path.includes('/users')) return 'pi pi-users'
  if (path.includes('/roles')) return 'pi pi-shield'
  if (path.includes('/incidents')) return 'pi pi-ban'
  if (path.includes('/documents')) return 'pi pi-file'
  if (path.includes('/contact-requests')) return "pi pi-envelope"
  if (path.includes('/tasks')) return 'pi pi-list-check'
  if (path.includes('/profile')) return 'pi pi-user'
  if (path.includes('/settings')) return 'pi pi-cog'
  if (path.includes('/reports')) return 'pi pi-flag-fill'
  return 'pi pi-gauge'
})

// Méthodes
const isActiveRoute = (href: string) => {
  return route.path === href
}

const hasActiveChild = (item: any) => {
  return item.children?.some((child: any) => isActiveRoute(child.href))
}

const toggleSubmenu = (name: string) => {
  const index = openSubmenus.value.indexOf(name)
  if (index > -1) {
    openSubmenus.value.splice(index, 1)
  } else {
    openSubmenus.value.push(name)
  }
}


// Fermer les menus quand on clique ailleurs
onMounted(() => {
  document.addEventListener('click', (e) => {
    const target = e.target as HTMLElement
    if (!target.closest('[data-user-menu]')) {
      showUserMenu.value = false
    }
  })
  // Si connecté, démarrer les notifications
  if (authStore.isAuthenticated) {
    startNotificationCheck()
  }
  // Écouter les événements de visibilité pour optimiser
  document.addEventListener('visibilitychange', handleVisibilityChange)
})

// Cleanup
onUnmounted(() => {
  stopNotificationCheck()
  document.removeEventListener('visibilitychange', handleVisibilityChange)
})

// Watcher pour gérer les changements d'auth
watch(() => authStore.isAuthenticated, (isAuth) => {
  if (isAuth) {
    startNotificationCheck()
  } else {
    stopNotificationCheck()
  }
})

// Optimisation de performance selon la visibilité
const handleVisibilityChange = () => {
  if (document.hidden) {
    // Page cachée : arrêter les vérifications
    stopNotificationCheck()
  } else if (authStore.isAuthenticated) {
    // Page visible : redémarrer
    startNotificationCheck()
  }
}
</script>

<style scoped>
/* Styles personnalisés si nécessaire */
.router-link-active {
  @apply bg-green-50 text-green-700;
}

.fade-enter-active,
.fade-leave-active {
  @apply transition-opacity duration-300;
}

.fade-enter-from,
.fade-leave-to {
  @apply opacity-0;
}
</style>